<template>
  <div class="flex flex-col items-center p-4 pb-24 mt-8 overflow-auto lg:mt-12">
    <div class="flex flex-col h-full gap-8 px-32 sm:flex-row">
      <TheCheckoutPriceCredits
        v-if="checkoutIdentifier === 'credits'"
        :selectedprice-id="props.priceId"
      />
      <TheCheckoutPriceCard
        v-if="checkoutIdentifier === 'subscription'"
        :selectedprice-id="props.priceId"
      />
      <div class="flex flex-col w-full gap-3">
        <div v-if="showCompanyNameField" class="flex flex-col">
          <label for="company" class="text-regular-14 hover:cursor-pointer">{{
            t('label.company')
          }}</label>
          <input
            v-model="companyName"
            class="border border-iq-beige rounded text-iq-beige bg-iq-grey-dark !py-[9px] !px-3 placeholder-iq-beige/60 text-regular-16"
            type="text"
            name="company"
            :placeholder="t('placeholder.company-name')"
          />
        </div>
        <div v-show="!showPayButton" id="address-element" />
        <div v-show="showPayButton" id="payment-element" />
        <div v-if="showProceedButton" class="flex flex-col -mt-1">
          <div class="hover:cursor-pointer">
            <input
              id="checkboxVat"
              v-model="isCheckedBuyAsCompany"
              class="mr-2 hover:cursor-pointer accent-iq-red"
              name="checkboxVat"
              type="checkbox"
            />
            <label
              for="checkboxVat"
              class="text-regular-14 hover:cursor-pointer"
              >{{ t('label.enter-Id') }}
            </label>
          </div>
          <input
            v-if="isCheckedBuyAsCompany"
            v-model="vatIdCustomer"
            class="border mt-1 border-iq-beige rounded text-iq-beige bg-iq-grey-dark !py-[9px] !px-3 placeholder-iq-beige/60 text-regular-16"
            :class="{ '!border-iq-red': hasErrorVat }"
            type="text"
            :placeholder="selectedVat?.placeholder"
          />
          <span
            v-if="isCheckedBuyAsCompany && hasErrorVat"
            class="!text-iq-red text-regular-12"
            >{{ t('label.invalid-Id') }}</span
          >
        </div>
        <div class="flex flex-col justify-end w-full pt-4">
          <UiButton
            v-if="showProceedButton"
            class="w-full !iq-button-success"
            @click="proceed"
            >{{ t('button.proceed') }} <TheSpinner v-if="loading"
          /></UiButton>
          <UiButton
            v-if="showPayButton"
            class="w-full !iq-button-success !h-auto flex flex-col"
            @click="pay"
            ><span v-if="!loading"
              >{{ t('button.buy') }}
              {{
                amount.toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                })
              }}</span
            >
            <span v-if="!loading" class="text-thin-12">{{
              t('button.paying')
            }}</span>
            <TheSpinner v-if="loading" /> </UiButton
          ><span v-if="showPayButton" class="mt-4 text-thin-12"
            >{{ t('label.purchase-confirm') }}
            <NuxtLink to="/privacy" class="underline" target="_blank">{{
              t('link.agb')
            }}</NuxtLink>
            {{ t('label.agb-confirm') }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  loadStripe,
  type Stripe,
  type StripeAddressElement,
  type StripeElements,
  type StripePaymentElement,
} from '@stripe/stripe-js';
import { toast } from 'vue-sonner';
import { ErrorCode } from '~/enum/error-code.enum';

const { t } = useI18n({
  useScope: 'local',
});
const props = defineProps<{
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  checkoutIdentifier: string;
  name: string;
  phone: string;
  priceId: string;
  vatId?: string;
  companyName?: string;
}>();

let addressElement: StripeAddressElement | undefined;
let elements: StripeElements | undefined;
let paymentElement: StripePaymentElement | undefined;
let stripe: Stripe | null;

const amount = ref(0);
const clientSecretRetrieved = ref();
const companyName = ref(props.companyName ?? '');
const hasErrorVat = ref(false);
const isCheckedBuyAsCompany = ref(false);
const loading = ref(false);
const name = ref<string>(props.name ?? '');
const phone = ref<string>(props.phone ?? '');
const showCompanyNameField = ref(true);
const showPayButton = ref(false);
const showProceedButton = ref(false);
const stripeUtil = stripeHelper(props.checkoutIdentifier);
const stripePublishable = useRuntimeConfig().public.stripePublishable;
const vatIdCustomer = ref('');

const address = ref<{
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}>(
  props.address ?? {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  }
);

const selectedVat = computed(() => {
  return vatIds.find((vat) => vat.code === address.value.country);
});

const vatIds = [
  {
    code: 'AT',
    label: t('country.at'),
    taxType: 'eu_vat',
    placeholder: 'ATU12345678',
  },
  {
    code: 'BE',
    label: t('country.be'),
    taxType: 'eu_vat',
    placeholder: 'BE0123456789',
  },
  {
    code: 'BG',
    label: t('country.bg'),
    taxType: 'eu_vat',
    placeholder: 'BG0123456789',
  },
  {
    code: 'CY',
    label: t('country.cy'),
    taxType: 'eu_vat',
    placeholder: 'CY12345678Z',
  },
  {
    code: 'CZ',
    label: t('country.cz'),
    taxType: 'eu_vat',
    placeholder: 'CZ1234567890',
  },
  {
    code: 'DE',
    label: t('country.de'),
    taxType: 'eu_vat',
    placeholder: 'DE123456789',
  },
  {
    code: 'DK',
    label: t('country.dk'),
    taxType: 'eu_vat',
    placeholder: 'DK12345678',
  },
  {
    code: 'EE',
    label: t('country.ee'),
    taxType: 'eu_vat',
    placeholder: 'EE123456789',
  },
  {
    code: 'ES',
    label: t('country.es'),
    taxType: 'eu_vat',
    placeholder: 'ESA1234567Z',
  },
  {
    code: 'FI',
    label: t('country.fi'),
    taxType: 'eu_vat',
    placeholder: 'FI12345678',
  },
  {
    code: 'FR',
    label: t('country.fr'),
    taxType: 'eu_vat',
    placeholder: 'FRAB123456789',
  },
  {
    code: 'GB',
    label: t('country.gb'),
    taxType: 'gb_vat',
    placeholder: 'GB123456789',
  },
  {
    code: 'GR',
    label: t('country.gr'),
    taxType: 'eu_vat',
    placeholder: 'EL123456789',
  },
  {
    code: 'HR',
    label: t('country.hr'),
    taxType: 'eu_vat',
    placeholder: 'HR12345678912',
  },
  {
    code: 'HU',
    label: t('country.hu'),
    taxType: 'eu_vat',
    placeholder: 'HU12345678',
  },
  {
    code: 'IE',
    label: t('country.ie'),
    taxType: 'eu_vat',
    placeholder: 'IE1234567AB',
  },
  {
    code: 'IT',
    label: t('country.it'),
    taxType: 'eu_vat',
    placeholder: 'IT12345678912',
  },
  {
    code: 'LT',
    label: t('country.lt'),
    taxType: 'eu_vat',
    placeholder: 'LT123456789123',
  },
  {
    code: 'LU',
    label: t('country.lu'),
    taxType: 'eu_vat',
    placeholder: 'LU12345678',
  },
  {
    code: 'LV',
    label: t('country.lv'),
    taxType: 'eu_vat',
    placeholder: 'LV12345678912',
  },
  {
    code: 'MT',
    label: t('country.mt'),
    taxType: 'eu_vat',
    placeholder: 'MT12345678',
  },
  {
    code: 'NL',
    label: t('country.nl'),
    taxType: 'eu_vat',
    placeholder: 'NL123456789B12',
  },
  {
    code: 'PL',
    label: t('country.pl'),
    taxType: 'eu_vat',
    placeholder: 'PL1234567890',
  },
  {
    code: 'PT',
    label: t('country.pt'),
    taxType: 'eu_vat',
    placeholder: 'PT123456789',
  },
  {
    code: 'RO',
    label: t('country.ro'),
    taxType: 'eu_vat',
    placeholder: 'RO1234567891',
  },
  {
    code: 'SE',
    label: t('country.se'),
    taxType: 'eu_vat',
    placeholder: 'SE123456789123',
  },
  {
    code: 'SI',
    label: t('country.si'),
    taxType: 'eu_vat',
    placeholder: 'SI12345678',
  },
  {
    code: 'SK',
    label: t('country.sk'),
    taxType: 'eu_vat',
    placeholder: 'SK1234567891',
  },
  {
    code: 'CH',
    label: t('country.ch'),
    taxType: 'ch_vat',
    placeholder: 'CHE-123.456.789 MWST',
  },
];

async function pay() {
  loading.value = true;
  if (stripe) {
    const success = await stripeUtil
      .confirmPaymentWithCard(stripe, elements, clientSecretRetrieved.value)
      .catch((error) => {
        toast.warning(t(error?.data?.statusMessage || ErrorCode.GENERIC));
        clearError();
        return false;
      });

    loading.value = false;

    if (!success) {
      return;
    }

    await navigateTo('/scan');
  }
}

async function proceed() {
  loading.value = true;
  showCompanyNameField.value = false;
  const customerId = await stripeUtil
    .saveAddressData(
      address.value,
      name.value,
      phone.value,
      selectedVat.value?.taxType,
      vatIdCustomer.value ?? '',
      companyName.value ?? ''
    )
    .catch((error) => {
      if (error.data?.statusMessage === ErrorCode.STRIPE_TAX_ERROR) {
        hasErrorVat.value = true;
      }
      console.error(error);
      toast.warning(t(error?.data?.statusMessage));
      clearError();
      loading.value = false;
      return;
    });

  if (customerId && !hasErrorVat.value) {
    try {
      const { clientSecret, amountDue } = await stripeUtil.payByCard(
        props.priceId,
        customerId
      );
      clientSecretRetrieved.value = clientSecret;

      amount.value = amountDue / 100;

      elements = stripe?.elements({
        clientSecret,
        appearance: stripeUtil.getStripeAppearanceObject().appearance,
        fonts: stripeUtil.getStripeAppearanceObject().fonts,
      });
      paymentElement = elements?.create('payment', {});
      paymentElement?.mount('#payment-element');

      showProceedButton.value = false;
      showPayButton.value = true;
    } catch (e: unknown) {
      const fetchError = e as { data?: { statusMessage?: string } };
      toast.warning(t(fetchError.data?.statusMessage ?? ErrorCode.GENERIC));
      clearError();
    }
  }
  loading.value = false;
}

onMounted(async () => {
  stripe = await loadStripe(stripePublishable as string);

  elements = stripe?.elements({
    appearance: stripeUtil.getStripeAppearanceObject().appearance,
    fonts: stripeUtil.getStripeAppearanceObject().fonts,
  });

  if (props.vatId) {
    vatIdCustomer.value = props.vatId;
    isCheckedBuyAsCompany.value = true;
  }

  // Create and mount the Address Element in shipping mode
  addressElement = elements?.create('address', {
    mode: 'billing',
    autocomplete: { mode: 'automatic' },
    fields: { phone: 'always' },
    defaultValues: {
      address: address.value,
      name: name.value,
      phone: phone.value,
    },
  });
  addressElement?.mount('#address-element');
  addressElement?.on('change', async (event) => {
    address.value.country = event.value.address.country;
    if (event.complete) {
      event.value.address.line2 = '';
      // Extract potentially complete address
      name.value = event.value.name ?? '';
      phone.value = event.value.phone ?? '';
      address.value = event.value.address;
      if (address.value && name.value) {
        showProceedButton.value = true;
      }
    }
  });
});

onUnmounted(async () => {
  addressElement?.destroy();
  paymentElement?.destroy();
});
</script>
<i18n lang="json">
{
  "de": {
    "label": {
      "company": "Firma (optional)",
      "enter-Id": "UstId eingeben?",
      "invalid-Id": "UstId ungültig",
      "purchase-confirm": "Mit dem Kauf bestätigst du, dass du die",
      "agb-confirm": "der IQ-ME GmbH gelesen und akzeptiert hast."
    },
    "button": {
      "proceed": "WEITER",
      "buy": "Kaufen",
      "paying": "(kostenpflichtig)"
    },
    "placeholder": {
      "company-name": "Firmenname"
    },
    "link": {
      "agb": "AGB"
    },
    "cuntry": {
      "at": "Österreich",
      "be": "Belgien",
      "bg": "Bulgarien",
      "cy": "Zypern",
      "cz": "Tschechische Republik",
      "de": "Deutschland",
      "dk": "Dänemark",
      "ee": "Estland",
      "es": "Spanien",
      "fi": "Finnland",
      "fr": "Frankreich",
      "gb": "Vereinigtes Königreich",
      "gr": "Griechenland",
      "hr": "Kroatien",
      "hu": "Ungarn",
      "ie": "Irland",
      "it": "Italien",
      "lt": "Litauen",
      "lu": "Luxemburg",
      "lv": "Lettland",
      "mt": "Malta",
      "nl": "Niederlande",
      "pl": "Polen",
      "pt": "Portugal",
      "ro": "Rumänien",
      "se": "Schweden",
      "si": "Slowenien",
      "sk": "Slowakei",
      "ch": "Schweiz"
    }
  },
  "en": {
    "label": {
      "company": "Company (optional)",
      "enter-Id": "Enter UstId?",
      "invalid-Id": "Invalid UstId",
      "purchase-confirm": "I confirm that I have read and accepted the",
      "agb-confirm": "the IQ-ME GmbH agreement."
    },
    "button": {
      "proceed": "PROCEED",
      "buy": "Buy",
      "paying": "(paying)"
    },
    "placeholder": {
      "company-name": "Company Name"
    },
    "link": {
      "agb": "AGB"
    },
    "cuntry": {
      "at": "Austria",
      "be": "Belgium",
      "bg": "Bulgaria",
      "cy": "Cyprus",
      "cz": "Czech Republic",
      "de": "Germany",
      "dk": "Denmark",
      "ee": "Estonia",
      "es": "Spain",
      "fi": "Finland",
      "fr": "France",
      "gb": "United Kingdom",
      "gr": "Greece",
      "hr": "Croatia",
      "hu": "Hungary",
      "ie": "Ireland",
      "it": "Italy",
      "lt": "Lithuania",
      "lu": "Luxembourg",
      "lv": "Latvia",
      "mt": "Malta",
      "nl": "Netherlands",
      "pl": "Poland",
      "pt": "Portugal",
      "ro": "Romania",
      "se": "Sweden",
      "si": "Slovenia",
      "sk": "Slovakia",
      "ch": "Switzerland"
    }
  }
}
</i18n>
